import { ApiFactory } from '../../../api/agwasuite-api-factory';
import { ApiSiteMaintenance } from '../../../api/core/api-site-maintenance';
import { ApiUserMaintenance } from '../../../api/core/api-user-maintenance';
import type {
  LoginSite,
  LoginSiteDomainScopeGrant,
  LoginGroup,
  SiteDomainScopeDto,
  UserDirectoryUserDto,
} from '../../../api/agwa-api-model';
import { userDirectoryInstance } from '../../../global-data/user-directory-instance';
import { terms } from '../../../language/terms';

export interface SiteWorkspaceState {
  loading: boolean;
  error: string | null;
  site: LoginSite | null;
  /** Stable document-proxy URL of the server-generated static site map image, if one exists. */
  mapImageUrl: string | null;
  /**
   * The owner's contact, resolved server-side from the linked staff member (site.ownerLoginUserId)
   * and carried on LoginSiteResult. All null when the site has no owner, and the phone null when
   * the owner has none. The email and phone are shown from here (D-151 rule 4); the name only
   * decides whether an owner is shown at all, because the owner is named by <user-name> from the
   * directory by site.ownerLoginUserId (rule 8), never by this string.
   */
  ownerName: string | null;
  ownerEmail: string | null;
  ownerPhoneNumber: string | null;
  group: LoginGroup | null;
  domains: LoginSiteDomainScopeGrant[];
  /** Whether this is a new site being created */
  isNew: boolean;
  /** Future: module-specific data like certificates, etc. */
  moduleData: Record<string, unknown>;
}

const DEFAULT_STATE: SiteWorkspaceState = {
  loading: false,
  error: null,
  site: null,
  mapImageUrl: null,
  ownerName: null,
  ownerEmail: null,
  ownerPhoneNumber: null,
  group: null,
  domains: [],
  isNew: false,
  moduleData: {},
};

export class SiteWorkspaceStore extends EventTarget {
  readonly siteId: string;
  readonly groupId: string;
  private state: SiteWorkspaceState = { ...DEFAULT_STATE };
  private siteLoaded = false;
  private loadingPromise: Promise<void> | null = null;

  /**
   * How many open pages are watching the owner's contact (watchOwnerContact), and the directory
   * subscription that serves them: the owner it is for, and how to end it. One subscription however
   * many pages watch, moved to the new owner whenever the site's owner changes.
   */
  private ownerWatchers = 0;
  /**
   * Counts every write of the site row OTHER than a re-read's own (a load, a save, a set-owner).
   * A re-read of the owner's contact remembers the count it started at and is dropped if anything
   * wrote the site since, so a late answer can never put back a site row older than one a save or
   * set-owner already showed (#398 C4 review 1, F4).
   */
  private siteWrites = 0;
  /**
   * Numbers each re-read. Only the latest one sent may apply, whichever order the answers land
   * in: two edits of the owner within one round trip send two re-reads, and the later one asked
   * after the later edit committed (review 2, R1). Counting the re-read's own apply in siteWrites
   * instead made the first answer to land discard the newer one.
   */
  private rereadSeq = 0;
  /** True while a re-read applies its answer, so that write is not counted in siteWrites. */
  private applyingReread = false;
  private ownerSubscription: { userId: string; stop: () => void } | null = null;

  constructor(siteId: string, groupId: string) {
    super();
    this.siteId = siteId;
    this.groupId = groupId;
  }

  get snapshot(): SiteWorkspaceState {
    return this.state;
  }

  async ensureHydrated() {
    if (this.siteLoaded || this.loadingPromise) {
      return this.loadingPromise ?? Promise.resolve();
    }
    // Handle "new" site creation mode
    if (this.siteId === 'new') {
      this.loadingPromise = this.fetchGroupForNewSite();
      try {
        await this.loadingPromise;
      } finally {
        this.loadingPromise = null;
      }
      return;
    }
    this.loadingPromise = this.fetchSiteBundle();
    try {
      await this.loadingPromise;
    } finally {
      this.loadingPromise = null;
    }
  }

  private async fetchGroupForNewSite() {
    this.updateState({ loading: true, error: null });
    try {
      const groupsResponse =
        await ApiFactory.authenticated(ApiUserMaintenance).getGroups();
      const group =
        groupsResponse.data?.groups?.find((g) => g.id === this.groupId) ?? null;

      this.siteLoaded = true;
      this.updateState({
        loading: false,
        isNew: true,
        site: this.createEmptySite(),
        group,
        domains: [],
        error: null,
      });
    } catch (error) {
      this.siteLoaded = true;
      this.updateState({
        loading: false,
        isNew: true,
        site: this.createEmptySite(),
        group: null,
        domains: [],
        error: null,
      });
    }
  }

  private createEmptySite(): LoginSite {
    return {
      id: '',
      loginGroupId: this.groupId,
      name: '',
      description: '',
      address: {} as LoginSite['address'],
      // A brand-new site starts unowned; an owner can be picked in the create form (it rides the
      // create DTO) or set later. Contact is sourced from the owner, not stored on the site.
      ownerLoginUserId: undefined,
      active: true,
      // Every site a person makes is one of the company's places, so it is visible. The hidden
      // Prime System IP site is the only exception and no form can reach it - the system creates it
      // when a site is registered as a supplier or a manufacturer (D-127 rule 1).
      visible: true,
      createdUtc: new Date().toISOString(),
      modifiedUtc: new Date().toISOString(),
      concurrencyToken: '',
    };
  }

  /**
   * Reads the site again and settles only on a read that STARTED after this call (#398 C13 review 1
   * F3). A load already in flight is awaited first and then a fresh one is made, because the
   * in-flight one may have begun before the change the caller is reacting to: Edit re-reading under
   * the lock it has just taken (@LOCK-040), another person's lock ending (@LOCK-041), or the
   * operation types changing. Joining it instead would show, or open the form on, values from before
   * that change. The company store's reload() is the same repair (C11 review 2 F1).
   */
  async refresh() {
    if (this.loadingPromise) {
      await this.loadingPromise;
    }
    this.siteLoaded = false;
    await this.ensureHydrated();
  }

  /**
   * Keeps the owner's email and phone current while a page shows them (#398 C4,
   * @CONTACT-011): when the site's owner is edited elsewhere, an open site page shows
   * the new values without a reload. Returns the function that stops watching.
   *
   * THE SIGNAL IS THE DIRECTORY'S, THE VALUES ARE THE SITE'S. The user directory hears every edit
   * of a person (the user-updated topic, primed by JobEditGroupUser), so it is what tells this store
   * the owner changed. The contact shown is then re-read from the site itself, because the server's
   * owner resolver is the one definition of the owner's contact (D-151 rule 4), so the page never
   * shows a value the site's own answer would not. A directory update that leaves the email and
   * phone as shown (a new avatar, a name) re-reads nothing.
   */
  watchOwnerContact(): () => void {
    this.ownerWatchers++;
    this.syncOwnerSubscription();
    let stopped = false;
    return () => {
      if (stopped) return;
      stopped = true;
      this.ownerWatchers--;
      this.syncOwnerSubscription();
    };
  }

  /**
   * Holds exactly one directory subscription, for the current owner, while anyone watches; none
   * otherwise. Called whenever watchers or state change, so a new owner moves it.
   */
  private syncOwnerSubscription() {
    const ownerId =
      this.ownerWatchers > 0 ? this.state.site?.ownerLoginUserId : undefined;
    if (this.ownerSubscription?.userId === ownerId) return;
    this.ownerSubscription?.stop();
    this.ownerSubscription = null;
    if (!ownerId) return;
    const stop = userDirectoryInstance().subscribe(ownerId, (user) =>
      this.onOwnerUpdated(user)
    );
    this.ownerSubscription = { userId: ownerId, stop };
  }

  /** A directory update for the owner: re-read the site's owner contact only if it moved. */
  private onOwnerUpdated(user: UserDirectoryUserDto) {
    if (user.loginUserId !== this.state.site?.ownerLoginUserId) return;
    const sameEmail = (user.emailAddress ?? null) === this.state.ownerEmail;
    const samePhone =
      (user.phoneNumber ?? null) === this.state.ownerPhoneNumber;
    if (sameEmail && samePhone) return;
    void this.rereadOwnerContact();
  }

  /**
   * Re-reads the site and takes its owner contact (and the site row it came with), leaving the
   * domains, group and loading state alone, so the page redraws the contact without a skeleton.
   * A failed read leaves the shown values as they were; the next edit, or a reload, tries again.
   */
  private async rereadOwnerContact() {
    const mine = ++this.rereadSeq;
    const startedAt = this.siteWrites;
    const response = await ApiFactory.authenticated(ApiSiteMaintenance).getSite(
      this.siteId
    );
    if (!response.success || !response.data?.site) return;
    // A later re-read was sent, or a save or set-owner wrote the site, while this was in flight:
    // theirs is the newer answer.
    if (mine !== this.rereadSeq || this.siteWrites !== startedAt) return;
    this.applyingReread = true;
    try {
      this.updateState({
        site: response.data.site,
        ownerName: response.data.ownerName ?? null,
        ownerEmail: response.data.ownerEmail ?? null,
        ownerPhoneNumber: response.data.ownerPhoneNumber ?? null,
      });
    } finally {
      this.applyingReread = false;
    }
  }

  private async fetchSiteBundle() {
    this.updateState({ loading: true, error: null });
    try {
      const siteApi = ApiFactory.authenticated(ApiSiteMaintenance);

      // Fetch site, group info, and site domains in parallel
      const [siteResponse, groupsResponse, domainsResponse] = await Promise.all(
        [
          siteApi.getSite(this.siteId),
          ApiFactory.authenticated(ApiUserMaintenance).getGroups(),
          siteApi.getSiteDomains(this.siteId),
        ]
      );

      console.debug('[SiteWorkspaceStore] domainsResponse:', domainsResponse);

      if (!siteResponse.success || !siteResponse.data?.site) {
        throw new Error(
          siteResponse.error ||
            `Unable to load ${terms.loginSite.lower} details.`
        );
      }

      const site = siteResponse.data.site;
      const group =
        groupsResponse.data?.groups?.find((g) => g.id === this.groupId) ?? null;

      // Convert SiteDomainScopeDto to LoginSiteDomainScopeGrant format
      const domains = this.convertToSiteDomainGrants(
        domainsResponse.data?.scopes ?? [],
        site
      );

      console.debug('[SiteWorkspaceStore] converted domains:', domains);

      this.siteLoaded = true;

      this.updateState({
        loading: false,
        site,
        mapImageUrl: siteResponse.data.mapImageUrl ?? null,
        // Owner contact is resolved server-side and travels on the result; surface it for display.
        ownerName: siteResponse.data.ownerName ?? null,
        ownerEmail: siteResponse.data.ownerEmail ?? null,
        ownerPhoneNumber: siteResponse.data.ownerPhoneNumber ?? null,
        group,
        domains,
        error: null,
      });
    } catch (error) {
      this.updateState({
        loading: false,
        error:
          (error as Error)?.message ??
          `Unable to load ${terms.loginSite.lower} details.`,
        site: null,
        group: null,
        domains: [],
      });
    }
  }

  /**
   * Convert SiteDomainScopeDto[] to LoginSiteDomainScopeGrant[] format
   * for compatibility with the workspace module system.
   */
  private convertToSiteDomainGrants(
    scopes: SiteDomainScopeDto[],
    site: LoginSite
  ): LoginSiteDomainScopeGrant[] {
    return scopes.map((scope) => ({
      scopeId: scope.scopeId,
      siteId: scope.loginSiteId,
      siteName: site.name,
      groupId: scope.loginGroupId,
      domainId: scope.domainId,
      domainCode: scope.domainCode,
      domainDisplayName: scope.domainDisplayName,
      domainInstanceId: scope.domainInstanceId,
      active: scope.active,
      roleSetIds: scope.roleAssignments?.map((r) => r.securityRoleSetId) ?? [],
    }));
  }

  /**
   * Save the site - creates if new, updates if existing.
   * Returns the saved site ID on success, null on failure.
   */
  async saveSite(updates: Partial<LoginSite>): Promise<string | null> {
    if (!this.state.site) return null;

    try {
      const api = ApiFactory.authenticated(ApiSiteMaintenance);

      if (this.state.isNew) {
        // Create new site (active defaults to true on server). A site may be created already owned
        // when the group has staff; the owner rides the create DTO. Empty -> undefined so the
        // server's Guid? binds to null (no owner) rather than failing to parse.
        const response = await api.createSite({
          loginGroupId: this.groupId,
          name: updates.name ?? this.state.site.name,
          description: updates.description ?? this.state.site.description,
          address: updates.address ?? this.state.site.address,
          ownerLoginUserId: updates.ownerLoginUserId || undefined,
          // Already trimmed by the form; empty means the site is created with no number.
          phoneNumber: updates.phoneNumber || undefined,
        });

        if (!response.success || !response.data?.site) {
          throw new Error(
            response.error || `Unable to create ${terms.loginSite.lower}.`
          );
        }

        const newSiteId = response.data.site.id;

        // Clear the "new" store from registry so next "new" gets a fresh store
        clearSiteWorkspaceStore('new', this.groupId);

        this.updateState({
          site: response.data.site,
          mapImageUrl: response.data.mapImageUrl ?? null,
          ownerName: response.data.ownerName ?? null,
          ownerEmail: response.data.ownerEmail ?? null,
          ownerPhoneNumber: response.data.ownerPhoneNumber ?? null,
          isNew: false,
        });
        return newSiteId;
      } else {
        // Update existing site. Owner uses FINAL-STATE semantics on the update DTO: the value sent
        // IS the owner after save, and empty -> undefined -> null CLEARS it. So it is passed through
        // exactly as the form intends (never `?? current`, which would make clearing impossible).
        const response = await api.updateSite(this.siteId, {
          id: this.siteId,
          name: updates.name ?? this.state.site.name,
          description: updates.description ?? this.state.site.description,
          address: updates.address ?? this.state.site.address,
          ownerLoginUserId: updates.ownerLoginUserId || undefined,
          active: updates.active ?? this.state.site.active,
          // The update input's partial semantics: undefined leaves the stored number, empty clears
          // it. The form seeds the field with the stored number, so it always sends one of the two.
          phoneNumber: updates.phoneNumber,
        });

        if (!response.success || !response.data?.site) {
          throw new Error(
            response.error || `Unable to save ${terms.loginSite.lower}.`
          );
        }

        this.updateState({
          site: response.data.site,
          mapImageUrl: response.data.mapImageUrl ?? null,
          ownerName: response.data.ownerName ?? null,
          ownerEmail: response.data.ownerEmail ?? null,
          ownerPhoneNumber: response.data.ownerPhoneNumber ?? null,
        });
        return response.data.site.id;
      }
    } catch (error) {
      console.error('Failed to save site:', error);
      return null;
    }
  }

  /**
   * Set or clear the site's owner as a standalone change (the read-only "Change Contact" button),
   * without a full site edit. Passing null clears the owner. Returns the error message on failure
   * (so the caller can surface it) or null on success; state is refreshed with the new owner contact.
   */
  async setOwner(ownerLoginUserId: string | null): Promise<string | null> {
    if (!this.state.site || this.state.isNew) {
      // A site must exist before its owner can be set standalone; new sites set owner via create.
      return `Save the ${terms.loginSite.lower} before setting a ${terms.siteOwner.lower}.`;
    }

    try {
      const response = await ApiFactory.authenticated(
        ApiSiteMaintenance
      ).setSiteOwner(this.siteId, ownerLoginUserId);

      if (!response.success || !response.data?.site) {
        return (
          response.error ||
          `Unable to set the ${terms.loginSite.lower} ${terms.siteOwner.lower}.`
        );
      }

      this.updateState({
        site: response.data.site,
        mapImageUrl: response.data.mapImageUrl ?? null,
        ownerName: response.data.ownerName ?? null,
        ownerEmail: response.data.ownerEmail ?? null,
        ownerPhoneNumber: response.data.ownerPhoneNumber ?? null,
      });
      return null;
    } catch (error) {
      return (
        (error as Error)?.message ??
        `Unable to set the ${terms.loginSite.lower} ${terms.siteOwner.lower}.`
      );
    }
  }

  private updateState(partial: Partial<SiteWorkspaceState>) {
    this.state = {
      ...this.state,
      ...partial,
    };
    if ('site' in partial && !this.applyingReread) this.siteWrites++;
    // A new owner (a save, a set-owner, a re-read) moves the contact watch to them.
    this.syncOwnerSubscription();
    this.dispatchEvent(new Event('change'));
  }
}

const storeRegistry = new Map<string, SiteWorkspaceStore>();

function storeKey(siteId: string, groupId: string): string {
  return `${groupId}:${siteId}`;
}

export function getSiteWorkspaceStore(
  siteId: string,
  groupId: string
): SiteWorkspaceStore {
  const key = storeKey(siteId, groupId);
  let store = storeRegistry.get(key);
  if (!store) {
    store = new SiteWorkspaceStore(siteId, groupId);
    storeRegistry.set(key, store);
  }
  return store;
}

export function clearSiteWorkspaceStore(siteId: string, groupId: string): void {
  const key = storeKey(siteId, groupId);
  storeRegistry.delete(key);
}
